import React, { useState, useEffect } from 'react';
import { Save, Factory } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import FormModalLayout from '../Common/FormModalLayout';
import { ensureRichTextValue, RichTextEditor } from '../RichTextEditor';
import Can from '../Common/Can';
import { P } from '../../constants/permissions';

const EconomicActivityModal = ({ economicActivity, onClose, onSave }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { register, control, handleSubmit, formState: { errors }, setValue, reset } = useForm();

  const isEditing = !!economicActivity;

  useEffect(() => {
    if (economicActivity) {
      setValue('code', economicActivity.code);
      setValue('name', economicActivity.name);
      setValue('description', ensureRichTextValue(economicActivity.description || ''));
      setValue('isExempt', economicActivity.isExempt === true || economicActivity.isExempt === 'true' ? 'true' : 'false');
      setValue('isActive', economicActivity.isActive === true || economicActivity.isActive === 'true' ? 'true' : 'false');
    } else {
      reset({
        code: '',
        name: '',
        description: '',
        isExempt: 'false',
        isActive: 'true',
      });
    }
  }, [economicActivity, setValue, reset]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await onSave(data);
    } catch (error) {
      console.error('Error saving economic activity:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormModalLayout
      title={isEditing ? 'Editar atividade econômica' : 'Nova atividade econômica'}
      subtitle="CNAE e flags de isenção para o catálogo."
      headerIcon={Factory}
      onClose={onClose}
      size="lg"
      historyEntityType="economic_activities"
      historyEntityId={economicActivity?.id}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline" disabled={isLoading}>
            Cancelar
          </button>
          <Can permission={P.CATALOG_ACCESS}>
            <button
              type="submit"
              form="economic-activity-modal-form"
              className="btn btn-primary inline-flex items-center justify-center gap-2"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-b-transparent" />
              ) : (
                <Save className="h-4 w-4 shrink-0" aria-hidden />
              )}
              {isEditing ? 'Atualizar' : 'Salvar'} atividade
            </button>
          </Can>
        </>
      }
    >
      <form id="economic-activity-modal-form" onSubmit={handleSubmit(onSubmit)} className="form-stack">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="form-group">
            <label className="form-label form-label-required">Código CNAE</label>
            <input
              type="text"
              className="form-input"
              placeholder="Ex: 0111-3/01"
              {...register('code', {
                required: 'Código CNAE é obrigatório',
                pattern: {
                  value: /^\d{4}-\d{1,2}\/\d{2}$/,
                  message: 'Formato inválido. Use: 0000-0/00',
                },
              })}
            />
            {errors.code && <p className="form-error">{errors.code.message}</p>}
          </div>

          <div className="form-group">
            <label className="form-label form-label-required">Nome</label>
            <input
              type="text"
              className="form-input"
              placeholder="Nome da atividade"
              {...register('name', { required: 'Nome é obrigatório' })}
            />
            {errors.name && <p className="form-error">{errors.name.message}</p>}
          </div>

          <div className="form-group">
            <label className="form-label">Isento de tributos</label>
            <select className="form-select" {...register('isExempt')}>
              <option value="false">Não</option>
              <option value="true">Sim</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Ativo no sistema</label>
            <select className="form-select" {...register('isActive')}>
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Descrição</label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <RichTextEditor
                value={field.value || ''}
                onChange={field.onChange}
                placeholder="Descrição detalhada da atividade econômica..."
              />
            )}
          />
        </div>
      </form>
    </FormModalLayout>
  );
};

export default EconomicActivityModal;
